import React, { useState, useEffect } from 'react';
import { Calendar, TrendingUp, Target, Award, Clock, CheckCircle, AlertCircle, Brain, Users, FileText, History } from 'lucide-react';
import PDIMultiFonteHistorico from './PDIMultiFonteHistorico';
import Autocomplete from '../ui/autocomplete';

const PDIMultiFonte = () => {
  const [loading, setLoading] = useState(false);
  const [selectedColaborador, setSelectedColaborador] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [periodoInicio, setPeriodoInicio] = useState('');
  const [periodoFim, setPeriodoFim] = useState('');
  const [pdiGerado, setPdiGerado] = useState(null);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('gerar'); // 'gerar' ou 'historico'

  useEffect(() => {
    // Definir período padrão (últimos 12 meses)
    const hoje = new Date();
    const umAnoAtras = new Date();
    umAnoAtras.setFullYear(hoje.getFullYear() - 1);
    
    setPeriodoFim(hoje.toISOString().split('T')[0]);
    setPeriodoInicio(umAnoAtras.toISOString().split('T')[0]);
  }, []);

  const gerarPDIMultiFonte = async () => {
    if (!selectedColaborador) {
      setError('Por favor, selecione um colaborador');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/pdi/generate-multisource?employee_id=${selectedColaborador}&periodo_inicio=${periodoInicio}&periodo_fim=${periodoFim}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setPdiGerado(data);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.detail || 'Não foi possível gerar o PDI inteligente. Tente novamente.');
      }
    } catch (error) {
      console.error('Erro ao gerar PDI:', error);
      setError('Erro ao conectar com o servidor');
    } finally {
      setLoading(false);
    }
  };

  const getPrioridadeColor = (prioridade) => {
    const colors = {
      'critica': 'bg-red-100 text-red-800 border-red-200',
      'alta': 'bg-orange-100 text-orange-800 border-orange-200',
      'media': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'baixa': 'bg-green-100 text-green-800 border-green-200'
    };
    return colors[prioridade?.toLowerCase()] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getTipoIcon = (tipo) => {
    const icons = {
      'curso': '📚',
      'mentoria': '🤝',
      'pratica': '💪',
      'projeto': '🎯',
      'treinamento': '🎓',
      'workshop': '👥'
    };
    return icons[tipo?.toLowerCase()] || '📋';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                PDI Multi-fonte Inteligente
              </h1>
              <p className="text-gray-600 mt-2">
                Geração de PDI consolidando Feedback 90º, 180º, 360º, Fit Cultural, 9Box e Avaliação de Desempenho
              </p>
            </div>
            <Brain className="w-16 h-16 text-purple-600" />
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-lg mb-6">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('gerar')}
              className={`flex-1 px-6 py-4 text-center font-medium transition-colors ${
                activeTab === 'gerar'
                  ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Target className="w-5 h-5" />
                <span>Gerar Novo PDI</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('historico')}
              className={`flex-1 px-6 py-4 text-center font-medium transition-colors ${
                activeTab === 'historico'
                  ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <History className="w-5 h-5" />
                <span>Histórico de PDIs</span>
              </div>
            </button>
          </div>
        </div>

        {/* Conteúdo da Aba Gerar */}
        {activeTab === 'gerar' && (
          <>
            {/* Formulário de Geração */}
            <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-blue-600" />
                Gerar Novo PDI
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Colaborador
                  </label>
                  <Autocomplete
                    value={selectedColaborador}
                    onValueChange={(value, employee) => {
                      setSelectedColaborador(value);
                      setSelectedEmployee(employee || null);
                      setPdiGerado(null);
                      setError('');
                    }}
                    endpoint={`${process.env.REACT_APP_BACKEND_URL}/api/employees-paged`}
                    searchParam="search"
                    getOptionLabel={(employee) => `${employee.nome || employee.name || 'Sem nome'}${employee.cargo ? ` · ${employee.cargo}` : ''}`}
                    getOptionValue={(employee) => employee.id}
                    placeholder="Busque um colaborador ativo"
                    extraParams={{ active_only: true }}
                    emptyMessage="Nenhum colaborador ativo encontrado"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Período Início
                  </label>
                  <input
                    type="date"
                    value={periodoInicio}
                    onChange={(e) => setPeriodoInicio(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Período Fim
                  </label>
                  <input
                    type="date"
                    value={periodoFim}
                    onChange={(e) => setPeriodoFim(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>

              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                  <AlertCircle className="w-5 h-5" />
                  {error}
            </div>
          )}

          <button
            onClick={gerarPDIMultiFonte}
            disabled={loading}
            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Gerando PDI com IA...
              </>
            ) : (
              <>
                <Brain className="w-5 h-5" />
                Gerar PDI Inteligente
              </>
            )}
          </button>
        </div>

        {/* Resultado PDI Gerado */}
        {pdiGerado && (
          <>
            {pdiGerado.pdi_gerado?.generation_mode === 'contingency' && (
              <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                A consolidação por IA está indisponível neste momento. Exibimos um plano estruturado a partir das fontes encontradas para que o acompanhamento não fique bloqueado.
              </div>
            )}
            {/* Estatísticas das Fontes */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-sm text-gray-600 mb-1">Total Fontes</div>
                <div className="text-2xl font-bold text-purple-600">{pdiGerado.total_fontes}</div>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-sm text-gray-600 mb-1">Feedback 90º</div>
                <div className="text-2xl font-bold text-blue-600">
                  {pdiGerado.dados_brutos?.total_acoes_feedback_90 || 0}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-sm text-gray-600 mb-1">Feedback 180º</div>
                <div className="text-2xl font-bold text-green-600">
                  {pdiGerado.dados_brutos?.total_acoes_feedback_180 || 0}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-sm text-gray-600 mb-1">Feedback 360º</div>
                <div className="text-2xl font-bold text-orange-600">
                  {pdiGerado.dados_brutos?.total_acoes_feedback_360 || 0}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-sm text-gray-600 mb-1">Fit Cultural</div>
                <div className="text-2xl font-bold text-pink-600">
                  {pdiGerado.dados_brutos?.total_acoes_fit_cultural || 0}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-sm text-gray-600 mb-1">9Box</div>
                <div className="text-xs font-medium text-indigo-600 mt-1">
                  {pdiGerado.dados_brutos?.nine_box_posicao || 'N/A'}
                </div>
              </div>
            </div>

            {/* Temas Prioritários */}
            {pdiGerado.pdi_gerado?.temas_prioritarios?.length > 0 && (
              <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                  Temas Prioritários Identificados
                </h2>
                <div className="flex flex-wrap gap-2">
                  {pdiGerado.pdi_gerado.temas_prioritarios.map((tema, index) => (
                    <span
                      key={index}
                      className="px-4 py-2 bg-purple-100 text-purple-800 rounded-full text-sm font-medium"
                    >
                      {tema}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Ações de Desenvolvimento */}
            {pdiGerado.pdi_gerado?.acoes?.length > 0 && (
              <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5 text-blue-600" />
                  Ações de Desenvolvimento Sugeridas pela IA
                </h2>

                <div className="space-y-4">
                  {pdiGerado.pdi_gerado.acoes.map((acao, index) => (
                    <div
                      key={index}
                      className="border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow"
                    >
                      {/* Header da Ação */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-start gap-3 flex-1">
                          <span className="text-3xl">{getTipoIcon(acao.tipo)}</span>
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-800 mb-1">
                              {acao.titulo}
                            </h3>
                            <p className="text-gray-600 text-sm">{acao.objetivo}</p>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getPrioridadeColor(acao.prioridade)}`}>
                          {acao.prioridade?.toUpperCase()}
                        </span>
                      </div>

                      {/* Justificativa */}
                      <div className="mb-3 p-3 bg-blue-50 rounded-lg">
                        <div className="text-xs font-semibold text-blue-800 mb-1 flex items-center gap-1">
                          <FileText className="w-3 h-3" />
                          Justificativa
                        </div>
                        <p className="text-sm text-blue-900">{acao.justificativa}</p>
                      </div>

                      {/* Detalhes */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div>
                          <div className="text-xs text-gray-500 mb-1">Tipo</div>
                          <div className="text-sm font-medium text-gray-800 capitalize">
                            {acao.tipo}
                          </div>
                        </div>

                        <div>
                          <div className="text-xs text-gray-500 mb-1">Prazo</div>
                          <div className="text-sm font-medium text-gray-800 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {acao.prazo_dias} dias
                          </div>
                        </div>

                        <div className="col-span-2">
                          <div className="text-xs text-gray-500 mb-1">Fontes</div>
                          <div className="flex flex-wrap gap-1">
                            {acao.fontes?.map((fonte, i) => (
                              <span
                                key={i}
                                className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs"
                              >
                                {fonte}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Impacto Esperado */}
                      {acao.impacto_esperado && (
                        <div className="mt-3 p-3 bg-green-50 rounded-lg">
                          <div className="text-xs font-semibold text-green-800 mb-1 flex items-center gap-1">
                            <TrendingUp className="w-3 h-3" />
                            Impacto Esperado
                          </div>
                          <p className="text-sm text-green-900">{acao.impacto_esperado}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Observações Gerais da IA */}
            {pdiGerado.pdi_gerado?.observacoes_gerais && (
              <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Brain className="w-5 h-5 text-purple-600" />
                  Análise da IA
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  {pdiGerado.pdi_gerado.observacoes_gerais}
                </p>
              </div>
            )}

            {/* Fontes Utilizadas */}
            <div className="bg-white rounded-xl shadow-lg p-6 mt-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-gray-600" />
                Fontes de Dados Utilizadas
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {pdiGerado.fontes_utilizadas?.map((fonte, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg"
                  >
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium text-gray-700 capitalize">
                      {fonte.replace('_', ' ')}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <div className="text-sm text-blue-800">
                  <strong>Pesos das Fontes:</strong>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                  {Object.entries(pdiGerado.pesos_fontes || {}).map(([fonte, peso]) => (
                    <div key={fonte} className="text-xs text-blue-900">
                      <span className="font-medium capitalize">{fonte.replace('_', ' ')}:</span>{' '}
                      {(peso * 100).toFixed(0)}%
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Estado Vazio */}
        {!pdiGerado && !loading && (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <Brain className="w-20 h-20 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              Nenhum PDI Gerado Ainda
            </h3>
            <p className="text-gray-500">
              Selecione um colaborador e clique em "Gerar PDI Inteligente" para começar
            </p>
          </div>
        )}
          </>
        )}

        {/* Conteúdo da Aba Histórico */}
        {activeTab === 'historico' && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <PDIMultiFonteHistorico 
              colaboradorId={selectedColaborador}
              colaboradorNome={selectedEmployee?.name || selectedEmployee?.nome || ''}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PDIMultiFonte;
